import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  IconButton,
} from '@mui/material';
import {
  LockOutlined as LockIcon,
  Fingerprint as FingerprintIcon,
  VpnKey as VpnKeyIcon,
  Login as LoginIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { loginStart, loginSuccess, loginFailure } from '../store/authSlice';
import { appAuthAPI } from '../services/api';

// Helper functions for WebAuthn
const base64UrlToUint8Array = (base64url) => {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/');
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
};

const uint8ArrayToBase64Url = (bytes) => {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '');
};

function Login() {
  const [authMethods, setAuthMethods] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  // Password login state
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // TOTP state
  const [showTOTP, setShowTOTP] = useState(false);
  const [totpToken, setTotpToken] = useState('');
  const [totpLoading, setTotpLoading] = useState(false);

  // WebAuthn state
  const [webauthnUsername, setWebauthnUsername] = useState('');
  const [webauthnLoading, setWebauthnLoading] = useState(false);

  // Forgot Password state
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
  const [forgotPasswordStep, setForgotPasswordStep] = useState(1); // 1=enter email, 2=enter code
  const [resetEmail, setResetEmail] = useState('');
  const [resetCode, setResetCode] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState(false);
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState('');
  const [forgotPasswordError, setForgotPasswordError] = useState('');

  // Password visibility state
  const [showPassword, setShowPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();

  // Load available authentication methods on mount
  useEffect(() => {
    loadAuthMethods();
  }, []);

  // Handle OIDC callback
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const code = params.get('code');
    const state = params.get('state');
    const providerId = sessionStorage.getItem('oidc_provider_id');

    if (code && state && providerId) {
      handleOIDCCallback(parseInt(providerId), code, state);
    }
  }, [location]);

  const loadAuthMethods = async () => {
    try {
      const response = await appAuthAPI.getAuthMethods();
      setAuthMethods(response.data);

      // Set default tab based on available methods
      if (response.data.webauthn && !response.data.password) {
        setActiveTab(1); // WebAuthn only
      } else if (response.data.oidc && !response.data.password && !response.data.webauthn) {
        setActiveTab(2); // OIDC only
      }
    } catch (err) {
      console.error('Failed to load auth methods:', err);
      setError('Failed to load authentication methods');
      // Default to password auth if loading fails
      setAuthMethods({ password: true });
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordLogin = async (e) => {
    e.preventDefault();
    setPasswordLoading(true);
    setError('');
    dispatch(loginStart());

    try {
      const response = await appAuthAPI.login(username, password);

      // Check if TOTP is required
      if (response.data.totp_required) {
        setShowTOTP(true);
        setPasswordLoading(false);
        return;
      }

      // Login successful
      localStorage.setItem('token', response.data.access);
      dispatch(
        loginSuccess({
          user: response.data.user,
          token: response.data.access,
        })
      );
      navigate('/');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Login failed';
      setError(errorMsg);
      dispatch(loginFailure(errorMsg));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleTOTPVerification = async (e) => {
    e.preventDefault();
    setTotpLoading(true);
    setError('');

    try {
      const response = await appAuthAPI.login(username, password, totpToken);

      // Login successful
      localStorage.setItem('token', response.data.access);
      dispatch(
        loginSuccess({
          user: response.data.user,
          token: response.data.access,
        })
      );
      navigate('/');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'TOTP verification failed';
      setError(errorMsg);
    } finally {
      setTotpLoading(false);
    }
  };

  const handleWebAuthnLogin = async () => {
    // Validate username is provided
    if (!webauthnUsername || webauthnUsername.trim() === '') {
      setError('Username is required for passkey authentication');
      return;
    }

    setWebauthnLoading(true);
    setError('');
    dispatch(loginStart());

    try {
      // Start WebAuthn authentication
      const startResponse = await appAuthAPI.startWebAuthnAuthentication(webauthnUsername);
      const options = startResponse.data; // Already parsed JSON object

      console.log('Authentication options received:', options);
      console.log('allowCredentials:', options.allowCredentials);
      console.log('timeout:', options.timeout);
      console.log('rpId:', options.rpId);

      // Convert challenge to Uint8Array
      const publicKeyOptions = {
        challenge: base64UrlToUint8Array(options.challenge),
        timeout: options.timeout || 300000,
        rpId: options.rpId,
        allowCredentials: (options.allowCredentials || []).map((cred) => ({
          type: 'public-key',
          id: base64UrlToUint8Array(cred.id),
          transports: cred.transports,
        })),
        userVerification: options.userVerification,
      };

      console.log('Converted publicKeyOptions:', publicKeyOptions);

      // Get credential from authenticator
      const credential = await navigator.credentials.get({
        publicKey: publicKeyOptions,
      });

      // Convert credential response to base64url
      const credentialResponse = {
        id: credential.id,
        rawId: uint8ArrayToBase64Url(new Uint8Array(credential.rawId)),
        type: credential.type,
        response: {
          clientDataJSON: uint8ArrayToBase64Url(
            new Uint8Array(credential.response.clientDataJSON)
          ),
          authenticatorData: uint8ArrayToBase64Url(
            new Uint8Array(credential.response.authenticatorData)
          ),
          signature: uint8ArrayToBase64Url(new Uint8Array(credential.response.signature)),
          userHandle: credential.response.userHandle
            ? uint8ArrayToBase64Url(new Uint8Array(credential.response.userHandle))
            : null,
        },
      };

      // Complete authentication
      const authResponse = await appAuthAPI.completeWebAuthnAuthentication(
        credential.id,
        credentialResponse,
        options.challenge
      );

      // Check if TOTP is required
      if (authResponse.data.totp_required) {
        setShowTOTP(true);
        setWebauthnLoading(false);
        return;
      }

      // Login successful
      localStorage.setItem('token', authResponse.data.access);
      dispatch(
        loginSuccess({
          user: authResponse.data.user,
          token: authResponse.data.access,
        })
      );
      navigate('/');
    } catch (err) {
      console.error('WebAuthn error:', err);
      const errorMsg =
        err.response?.data?.error || err.message || 'WebAuthn authentication failed';
      setError(errorMsg);
      dispatch(loginFailure(errorMsg));
    } finally {
      setWebauthnLoading(false);
    }
  };

  const handleOIDCLogin = async (providerId, providerName) => {
    setError('');
    dispatch(loginStart());

    try {
      const redirectUri = `${window.location.origin}/login`;
      const response = await appAuthAPI.startOIDCAuth(providerId, redirectUri);

      // Store provider ID for callback
      sessionStorage.setItem('oidc_provider_id', providerId);

      // Redirect to OIDC provider
      window.location.href = response.data.authorization_url;
    } catch (err) {
      const errorMsg = err.response?.data?.error || `Failed to start ${providerName} authentication`;
      setError(errorMsg);
      dispatch(loginFailure(errorMsg));
    }
  };

  const handleOIDCCallback = async (providerId, code, state) => {
    setLoading(true);
    setError('');

    try {
      const redirectUri = `${window.location.origin}/login`;
      const response = await appAuthAPI.completeOIDCAuth(providerId, code, state, redirectUri);

      // Clear stored provider ID
      sessionStorage.removeItem('oidc_provider_id');

      // Login successful
      localStorage.setItem('token', response.data.access);
      dispatch(
        loginSuccess({
          user: response.data.user,
          token: response.data.access,
        })
      );

      // Clear URL parameters and navigate
      window.history.replaceState({}, document.title, '/login');
      navigate('/');
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'OIDC authentication failed';
      setError(errorMsg);
      dispatch(loginFailure(errorMsg));
      sessionStorage.removeItem('oidc_provider_id');
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container component="main" maxWidth="xs">
        <Box sx={{ mt: 8, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  const showTabs =
    (authMethods?.password ? 1 : 0) +
      (authMethods?.webauthn ? 1 : 0) +
      (authMethods?.oidc ? 1 : 0) >
    1;

  const handleForgotPasswordRequest = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    try {
      await appAuthAPI.requestPasswordResetEmail(resetEmail);
      setForgotPasswordSuccess(`A password reset code has been sent to ${resetEmail}`);
      setForgotPasswordStep(2);
    } catch (err) {
      // Generic message for security
      setForgotPasswordSuccess(`If an account with that email exists, a reset code has been sent.`);
      setForgotPasswordStep(2);
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setForgotPasswordLoading(true);
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    // Validate passwords match
    if (newPassword !== confirmPassword) {
      setForgotPasswordError('Passwords do not match');
      setForgotPasswordLoading(false);
      return;
    }

    // Validate password length
    if (newPassword.length < 8) {
      setForgotPasswordError('Password must be at least 8 characters');
      setForgotPasswordLoading(false);
      return;
    }

    try {
      await appAuthAPI.verifyPasswordResetCode(resetEmail, resetCode, newPassword);
      setForgotPasswordSuccess('Password reset successfully! You can now log in with your new password.');
      setTimeout(() => {
        handleCloseForgotPassword();
      }, 2000);
    } catch (err) {
      setForgotPasswordError(err.response?.data?.error || 'Failed to reset password. Please try again.');
    } finally {
      setForgotPasswordLoading(false);
    }
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordStep(1);
    setResetEmail('');
    setResetCode('');
    setNewPassword('');
    setConfirmPassword('');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) =>
          theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #0A0B0D 0%, #1F2123 100%)'
            : 'linear-gradient(135deg, #F5F6F7 0%, #E8EAED 100%)',
        py: 4,
      }}
    >
      <Container component="main" maxWidth="xs">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 5 },
            width: '100%',
            borderRadius: 4,
            border: (theme) =>
              theme.palette.mode === 'dark'
                ? '1px solid rgba(255, 255, 255, 0.08)'
                : '1px solid rgba(0, 0, 0, 0.08)',
            boxShadow: (theme) =>
              theme.palette.mode === 'dark'
                ? '0 8px 32px rgba(0, 0, 0, 0.6)'
                : '0 8px 32px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box
              sx={{
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #22A7F0 0%, #FF8A3D 100%)'
                    : 'linear-gradient(135deg, #0877A6 0%, #FF6E00 100%)',
                color: 'white',
                borderRadius: '50%',
                p: 2,
                mb: 2,
                boxShadow: (theme) =>
                  theme.palette.mode === 'dark'
                    ? '0 4px 16px rgba(34, 167, 240, 0.3)'
                    : '0 4px 16px rgba(8, 119, 166, 0.3)',
              }}
            >
              <LockIcon fontSize="large" />
            </Box>
            <Typography
              component="h1"
              variant="h5"
              sx={{
                mb: 1,
                fontWeight: 700,
                background: (theme) =>
                  theme.palette.mode === 'dark'
                    ? 'linear-gradient(135deg, #22A7F0 0%, #FF8A3D 100%)'
                    : 'linear-gradient(135deg, #0877A6 0%, #FF6E00 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              Samba4 Web UI
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Sign in to manage your Active Directory
            </Typography>

            {error && (
              <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
                {error}
              </Alert>
            )}

            {showTabs && (
              <Tabs
                value={activeTab}
                onChange={(e, newValue) => setActiveTab(newValue)}
                variant="fullWidth"
                sx={{ width: '100%', mb: 2 }}
              >
                {authMethods?.password && <Tab label="Password" icon={<VpnKeyIcon />} />}
                {authMethods?.webauthn && <Tab label="Passkey" icon={<FingerprintIcon />} />}
                {authMethods?.oidc && <Tab label="SSO" icon={<LoginIcon />} />}
              </Tabs>
            )}

            {/* Password Login Tab */}
            {authMethods?.password && activeTab === 0 && !showTOTP && (
              <Box component="form" onSubmit={handlePasswordLogin} sx={{ width: '100%' }}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="Username or Email"
                  name="username"
                  autoComplete="username"
                  autoFocus
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  disabled={passwordLoading}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  autoComplete="current-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  disabled={passwordLoading}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Box sx={{ textAlign: 'right', mt: 1 }}>
                  <Button
                    size="small"
                    onClick={() => setForgotPasswordOpen(true)}
                    sx={{ textTransform: 'none' }}
                  >
                    Forgot Password?
                  </Button>
                </Box>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2 }}
                  disabled={passwordLoading}
                >
                  {passwordLoading ? <CircularProgress size={24} /> : 'Sign In'}
                </Button>
              </Box>
            )}

            {/* TOTP Verification */}
            {showTOTP && (
              <Box component="form" onSubmit={handleTOTPVerification} sx={{ width: '100%' }}>
                <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
                  Enter the 6-digit code from your authenticator app
                </Typography>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="totp"
                  label="TOTP Code"
                  name="totp"
                  autoComplete="one-time-code"
                  autoFocus
                  value={totpToken}
                  onChange={(e) => setTotpToken(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  disabled={totpLoading}
                  inputProps={{ maxLength: 6, pattern: '[0-9]*' }}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 3, mb: 1 }}
                  disabled={totpLoading || totpToken.length !== 6}
                >
                  {totpLoading ? <CircularProgress size={24} /> : 'Verify'}
                </Button>
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setShowTOTP(false);
                    setTotpToken('');
                    setPassword('');
                  }}
                  disabled={totpLoading}
                >
                  Back
                </Button>
              </Box>
            )}

            {/* WebAuthn/Passkey Login Tab */}
            {authMethods?.webauthn &&
              ((showTabs && activeTab === (authMethods?.password ? 1 : 0)) ||
                (!showTabs && !authMethods?.password)) && (
                <Box sx={{ width: '100%' }}>
                  <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="webauthn-username"
                    label="Username"
                    name="webauthn-username"
                    autoComplete="username"
                    value={webauthnUsername}
                    onChange={(e) => setWebauthnUsername(e.target.value)}
                    disabled={webauthnLoading}
                  />
                  <Button
                    fullWidth
                    variant="contained"
                    onClick={handleWebAuthnLogin}
                    sx={{ mt: 3, mb: 2 }}
                    disabled={webauthnLoading}
                    startIcon={<FingerprintIcon />}
                  >
                    {webauthnLoading ? <CircularProgress size={24} /> : 'Sign in with Passkey'}
                  </Button>
                </Box>
              )}

            {/* OIDC/SSO Login Tab */}
            {authMethods?.oidc &&
              authMethods?.oidc_providers?.length > 0 &&
              ((showTabs &&
                activeTab ===
                  (authMethods?.password ? 1 : 0) + (authMethods?.webauthn ? 1 : 0)) ||
                (!showTabs && !authMethods?.password && !authMethods?.webauthn)) && (
                <Box sx={{ width: '100%' }}>
                  <Typography variant="body2" sx={{ mb: 2, textAlign: 'center' }}>
                    Sign in with your organization account
                  </Typography>
                  <List sx={{ width: '100%' }}>
                    {authMethods.oidc_providers.map((provider) => (
                      <ListItem key={provider.id} disablePadding>
                        <ListItemButton
                          onClick={() => handleOIDCLogin(provider.id, provider.name)}
                        >
                          <ListItemIcon>
                            <LoginIcon />
                          </ListItemIcon>
                          <ListItemText primary={`Sign in with ${provider.name}`} />
                        </ListItemButton>
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
          </Box>
        </Paper>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 3, textAlign: 'center', display: 'block' }}
        >
          Secure access to your domain controller
        </Typography>
      </Container>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={() => !forgotPasswordLoading && handleCloseForgotPassword()}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {forgotPasswordStep === 1 ? 'Reset Password' : 'Enter Reset Code'}
        </DialogTitle>
        <DialogContent>
          {forgotPasswordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {forgotPasswordError}
            </Alert>
          )}
          {forgotPasswordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {forgotPasswordSuccess}
            </Alert>
          )}

          {forgotPasswordStep === 1 ? (
            <Box component="form" onSubmit={handleForgotPasswordRequest} sx={{ mt: 2 }}>
              <TextField
                required
                fullWidth
                type="email"
                label="Email Address"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                autoFocus
                disabled={forgotPasswordLoading}
              />
              <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                Enter your email address and we'll send you a code to reset your password.
              </Typography>
            </Box>
          ) : (
            <Box component="form" onSubmit={handlePasswordReset} sx={{ mt: 2 }}>
              <TextField
                required
                fullWidth
                label="Reset Code"
                value={resetCode}
                onChange={(e) => setResetCode(e.target.value)}
                autoFocus
                disabled={forgotPasswordLoading}
                placeholder="Enter 6-digit code"
                sx={{ mb: 2 }}
              />
              <TextField
                required
                fullWidth
                type={showNewPassword ? 'text' : 'password'}
                label="New Password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={forgotPasswordLoading}
                sx={{ mb: 2 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowNewPassword(!showNewPassword)}
                        edge="end"
                      >
                        {showNewPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <TextField
                required
                fullWidth
                type={showConfirmPassword ? 'text' : 'password'}
                label="Confirm Password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                disabled={forgotPasswordLoading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                        edge="end"
                      >
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseForgotPassword}
            disabled={forgotPasswordLoading}
          >
            Cancel
          </Button>
          {forgotPasswordStep === 1 ? (
            <Button
              onClick={handleForgotPasswordRequest}
              variant="contained"
              disabled={forgotPasswordLoading || !resetEmail}
            >
              {forgotPasswordLoading ? <CircularProgress size={24} /> : 'Send Code'}
            </Button>
          ) : (
            <>
              <Button
                onClick={() => {
                  setForgotPasswordStep(1);
                  setForgotPasswordError('');
                  setForgotPasswordSuccess('');
                }}
                disabled={forgotPasswordLoading}
              >
                Back
              </Button>
              <Button
                onClick={handlePasswordReset}
                variant="contained"
                disabled={forgotPasswordLoading || !resetCode || !newPassword || !confirmPassword}
              >
                {forgotPasswordLoading ? <CircularProgress size={24} /> : 'Reset Password'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default Login;
